import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';

const About: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section 
        className="relative pt-16 bg-cover"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(https://sonicmakers-uploads.lon1.digitaloceanspaces.com/public/backgrounds/about.gif)',
          backgroundPosition: 'center bottom',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat'
        }}
      >

        
        <div className="container mx-auto px-4 text-center vertical-bottom relative z-10 flex flex-col justify-center h-full" style={{ paddingTop: '80px' }}>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-fade-in mt-24">
            {t('about.hero_title')}
            <span className="block bg-gradient-to-r from-purple-400 to-pink-600 bg-clip-text text-transparent leading-tight py-2">
              SONIC MAKERS
            </span>
          </h1>
          <p className="text-xl text-gray-300 mb-2 max-w-3xl mx-auto animate-fade-in">
            {t('about.hero_subtitle')}
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-900/50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-600 rounded-full mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-purple-400">
                {t('about.london_based')}
              </h3>
              <p className="text-gray-400">
                {t('about.london_text')}
              </p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-600 rounded-full mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-purple-400">
                {t('about.global_reach')}
              </h3>
              <p className="text-gray-400">
                {t('about.global_text')}
              </p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-600 rounded-full mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-purple-400">
                {t('about.music_driven')}
              </h3>
              <p className="text-gray-400">
                {t('about.music_text')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center text-white">
              {t('about.our_story_title')}
            </h2>
            <div className="space-y-6 text-lg text-gray-300 leading-relaxed">
              <p>
                {t('about.our_story_text')}
              </p>
              <p>
                {t('about.our_story_text_2')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Image Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <img
              src="https://sonicmakers-uploads.lon1.digitaloceanspaces.com/public/images/about-mission-image.gif"
              alt="Our Mission"
              className="w-full h-auto rounded-lg shadow-2xl opacity-80"
            />
          </div>
        </div>
      </section>

      {/* Our Mission Section */}
      <section className="py-20 bg-gray-900/50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-12 text-white">
              {t('about.our_mission_title')}
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed">
              {t('about.our_mission_text')}
            </p>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-20 bg-gray-800/50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center text-white">
              {t('about.our_values_title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              <div className="p-8 bg-gray-900/50 rounded-lg">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    {t('about.creativity_title')}
                  </h3>
                </div>
                <p className="text-gray-400 leading-relaxed">
                  {t('about.creativity_text')}
                </p>
              </div>

              <div className="p-8 bg-gray-900/50 rounded-lg">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    {t('about.quality_title')}
                  </h3>
                </div>
                <p className="text-gray-400 leading-relaxed">
                  {t('about.quality_text')}
                </p>
              </div>

              <div className="p-8 bg-gray-900/50 rounded-lg">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    {t('about.community_title')}
                  </h3>
                </div>
                <p className="text-gray-400 leading-relaxed">
                  {t('about.community_text')}
                </p>
              </div>

              <div className="p-8 bg-gray-900/50 rounded-lg">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    {t('about.innovation_title')}
                  </h3>
                </div>
                <p className="text-gray-400 leading-relaxed">
                  {t('about.innovation_text')}
                </p>
              </div>
            </div>
            
            {/* Values Images */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <img
                src="https://sonicmakers-uploads.lon1.digitaloceanspaces.com/public/images/about-values-image-1.png"
                alt="Our Values"
                className="w-full h-auto rounded-lg shadow-xl opacity-75"
              />
              <img
                src="https://sonicmakers-uploads.lon1.digitaloceanspaces.com/public/images/about-values-image-2.png"
                alt="Our Values"
                className="w-full h-auto rounded-lg shadow-xl opacity-75"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Team Section */}
      <section className="py-20 bg-gray-900/50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-12 text-white">
              {t('about.our_team_title')}
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-12">
              {t('about.our_team_text')}
            </p>
            
            {/* Team Photos */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <img
                src="https://sonicmakers-uploads.lon1.digitaloceanspaces.com/public/images/about-member-1.png"
                alt="Team"
                className="w-full h-64 object-cover rounded-lg shadow-xl opacity-85"
              />
              <img
                src="https://sonicmakers-uploads.lon1.digitaloceanspaces.com/public/images/about-member-2.png"
                alt="Team"
                className="w-full h-64 object-cover rounded-lg shadow-xl opacity-85"
              />
              <img
                src="https://sonicmakers-uploads.lon1.digitaloceanspaces.com/public/images/about-member-3.png"
                alt="Team"
                className="w-full h-64 object-cover rounded-lg shadow-xl opacity-85"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Join Us Section */}
      <section className="py-20 bg-gradient-to-b from-purple-900/20 to-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6 text-white">
              {t('about.join_us_title')}
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed mb-12">
              {t('about.join_us_text')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/plugins"
                className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center justify-center"
              >
                {t('about.explore_plugins_button')}
                <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
              <a
                href="mailto:info@sonicmakers.com"
                className="border border-purple-600 text-purple-400 hover:bg-purple-600 hover:text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center justify-center"
              >
                {t('about.contact_us_button')}
                <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About; 